/**
 * Contains all our accepted barcode types
 */
export const VALID_BARCODE_PATTERNS: Record<string, RegExp> = {
  "EAN-8": /^[0-9]{8}$/,
  "EAN-13": /^[0-9]{13}$/,
  "UPC-A": /^[0-9]{12}$/,
  "UPC-E": /^[0-9]{6,8}$/, // Can be 6 or 8 depending on format
  "Code 39": /^[0-9A-Z\-\.\$\/\+% ]+$/, // Supports alphanumeric & special chars
  "Code 128": /^[\x00-\x7F]+$/, // Any ASCII (scanner-dependent)
  "Data Matrix": /^(?:\]d2|\]Q3)?(?:(?:<GS>)|[\x20-\x7E]|\x1D)+$/i,
};

export const INTERNAL_BARCODE_PATTERNS: Record<string, RegExp> = {
  RX_ORDER_SKU_ID: /^R\d+$/,
  SSI_SCHAEFER_CARTON: /^500[0-9]{9}$/,
  SHELF_NUMBER: /^H[a-z]{2}[0-9]{2}[a-z]{1}[0-9]{1}$/i,
};

export const ALL_VALID_BARCODE_FORMATS = {
  ...INTERNAL_BARCODE_PATTERNS, // prioritizing our internal patterns.
  ...VALID_BARCODE_PATTERNS,
} as const;

export type BarcodeFormat = keyof typeof ALL_VALID_BARCODE_FORMATS;

export const UNKNOWN_BARCODE_TYPE = "UNKNOWN";
/**
 * Contains all our accepted barcode types
 * @param {string} code - The value to test against our accepted barcodes.
 * @returns {string} Return matching barcode type || UNKNOWN_BARCODE_TYPE
 */
export const detectBarcodeType = (code: string): string => {
  for (const [format, regex] of Object.entries({
    ...INTERNAL_BARCODE_PATTERNS,
    ...VALID_BARCODE_PATTERNS,
  })) {
    if (regex.test(code)) {
      return format;
    }
  }
  return UNKNOWN_BARCODE_TYPE;
};

export const isValidBarcodeCharacter = (char: string) => /^[a-zA-Z0-9\-\.\$\/\+% ]$/.test(char);

/**
 * Checks if provided data is an valid error response
 * @param {{ message: string }} data - The data we'll validate
 * @returns {boolean} Return boolean
 */
export const isErrorResponse = (data: any): data is { message: string } => {
  return data && typeof data === "object" && "message" in data;
};

export const qty = (n?: number) => n ?? 0;

export const remainingVerification = (confirmed?: number, verified?: number) =>
  qty(confirmed) - qty(verified);

export const remainingDelivery = (confirmed?: number, delivered?: number) =>
  qty(confirmed) - qty(delivered);

export const isSatisfied = (confirmed?: number, current?: number) => qty(current) >= qty(confirmed);
